<template>
    <RedesignBaseModal
        :visible="visible"
        :title="$t('widget.ai.setupTemplate')"
        width="70%"
        @update:visible="onClose"
        @close="cancel"
    >
        <div class="ai-parameter-modal">
            <!-- Loading State -->
            <div
                v-if="isLoading"
                class="flex items-center justify-center py-8"
            >
                <span class="text-gray-500">{{ $t('common.loading') }}...</span>
            </div>

            <template v-else>
                <!-- Mode Selection Radio Group -->
                <div class="mode-selector mb-6">
                    <RedesignFormLabel
                        :label="$t('widget.ai.selectMode')"
                        class="mb-3"
                    />
                    <RedesignRadioGroup v-model="selectedMode">
                        <RedesignRadioButton
                            v-for="mode in modeOptions"
                            :key="mode.value"
                            :label="mode.value"
                        >
                            {{ mode.label }}
                        </RedesignRadioButton>
                    </RedesignRadioGroup>
                </div>

                <!-- Single Selection Mode -->
                <div
                    v-if="selectedMode === 'single'"
                    class="single-select-container"
                >
                    <AiParameterSelect
                        key="single-select"
                        :value="singleValue"
                        :label="$t('common.selectParameter')"
                        :placeholder="$t('common.selectParameter')"
                        :parameter-groups="parameterGroups"
                        :custom-parameters="customParameters"
                        @input="singleValue = $event"
                    />

                    <AiParameterCustomInput
                        :custom-parameters="customParameters"
                        :parameter-groups="parameterGroups"
                        class="mt-4"
                        @add="addCustomParameter"
                        @remove="removeCustomParameter"
                    />
                </div>

                <!-- Multiple Selection Mode -->
                <div
                    v-else-if="selectedMode === 'multiple'"
                    class="multiple-select-container"
                >
                    <AiParameterSelect
                        key="multiple-select"
                        :value="multipleValue"
                        :label="$t('common.selectParameters')"
                        :placeholder="$t('common.selectParameters')"
                        :parameter-groups="parameterGroups"
                        :custom-parameters="customParameters"
                        multiple
                        @input="multipleValue = $event"
                    />

                    <AiParameterCustomInput
                        :custom-parameters="customParameters"
                        :parameter-groups="parameterGroups"
                        class="mt-4"
                        @add="addCustomParameter"
                        @remove="removeCustomParameter"
                    />
                </div>
                <div
                    v-else-if="selectedMode === 'template'"
                    class="template-editor-container"
                >
                    <!-- Markdown Editor with parameter toolbar slot -->
                    <MarkdownEditor
                        ref="markdownEditorRef"
                        :value="templateValue"
                        mode="edit"
                        editor-type="wysiwyg"
                        height="350px"
                        :enable-parameters="true"
                        @update:value="onTemplateChange"
                    >
                        <template #toolbar>
                            <MarkdownParameterToolbar
                                :parameter-groups="allParameterGroups"
                                :templates="aiTemplates"
                                :is-loading-templates="isLoadingTemplates"
                                :label="$t('widget.insertParameter')"
                                @insert="onInsertParameter"
                                @select-template="onTemplateSelected"
                            />
                        </template>
                    </MarkdownEditor>
                    <AiParameterCustomInput
                        :custom-parameters="customParameters"
                        :parameter-groups="parameterGroups"
                        class="mt-4"
                        @add="addCustomParameter"
                        @remove="removeCustomParameter"
                    />
                </div>

                <div class="mt-4">
                    <RedesignCheckbox v-model="addCallLink">
                        {{ $t('widget.ai.displayCallLinks') }}
                    </RedesignCheckbox>
                </div>
            </template>
        </div>

        <template #footer>
            <div class="w-full flex items-center justify-end gap-3">
                <RedesignBaseButton
                    type="outline"
                    color="secondary"
                    @click="cancel"
                >
                    {{ $t('common.cancel') }}
                </RedesignBaseButton>

                <RedesignBaseButton
                    @click="save"
                >
                    {{ $t('common.save') }}
                </RedesignBaseButton>
            </div>
        </template>
    </RedesignBaseModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import cloneDeep from 'lodash/cloneDeep'

import RedesignBaseModal from '@/components/RedesignComponents/Modal/RedesignBaseModal.vue'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import RedesignRadioGroup from '@/components/RedesignBaseComponents/Inputs/RedesignRadioGroup.vue'
import RedesignRadioButton from '@/components/RedesignBaseComponents/Inputs/RedesignRadioButton.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignCheckbox from '@/components/RedesignBaseComponents/Inputs/RedesignCheckbox.vue'
import MarkdownEditor from '@/components/RedesignBaseComponents/MarkdownEditor.vue'
import MarkdownParameterToolbar from '@/components/RedesignBaseComponents/MarkdownParameterToolbar.vue'
import AiParameterSelect from '@/components/RedesignComponents/Widgets/AiParameterSelect.vue'
import AiParameterCustomInput, { type ParameterOption, type ParameterGroup } from '@/components/RedesignComponents/Widgets/AiParameterCustomInput.vue'
import { WidgetConfig } from '@/types/dashboard'
import { widgetParameterApi, type AiTemplate } from '@/api/widgetParameterApi'
import i18n from '@/i18n'


type AiParameterMode = 'single' | 'multiple' | 'template'

/**
 * Simplified data structure for AI Parameter value:
 * - single: { type: 'single', data: 'parameter_key', addCallLink?: boolean }
 * - multiple: { type: 'multiple', data: [...], addCallLink?: boolean } (ordered by drag reorder)
 * - template: { type: 'template', data: 'markdown content', addCallLink?: boolean }
 */
interface AiParameterValueJson {
    type: AiParameterMode
    data: string | string[]
    addCallLink?: boolean
    customParameters?: ParameterOption[] // User-added custom parameters (kept for reference)
}

const props = defineProps<{
    visible: boolean
    config: WidgetConfig
    parameterGroups?: ParameterGroup[]
    isLoading?: boolean
}>()

const emit = defineEmits<{
    (e: 'update:visible', v: boolean): void
    (e: 'save', value: WidgetConfig): void
}>()

const { closeWithConfirm } = useConfirmBeforeClose()
const markdownEditorRef = ref<InstanceType<typeof MarkdownEditor> | null>(null)

// Mode options for the radio group
const modeOptions = computed(() => [
    {
        value: 'single',
        label: i18n.t('widget.ai.mode.single')
    },
    {
        value: 'multiple',
        label: i18n.t('widget.ai.mode.multiple')
    },
    {
        value: 'template',
        label: i18n.t('widget.ai.mode.template')
    }
])

const parameterGroups = computed(() => props.parameterGroups ?? [])

// Combined parameter groups (including custom parameters) for the markdown editor
const allParameterGroups = computed<ParameterGroup[]>(() => {
    const groups = [ ...parameterGroups.value ]

    if (customParameters.value.length > 0) {
        groups.push({
            label: String(i18n.t('common.customParameters')),
            options: customParameters.value
        })
    }

    return groups
})

// Local state (draft values)
const selectedMode = ref<AiParameterMode>('single')
const singleValue = ref<string>('')
const multipleValue = ref<string[]>([])
const templateValue = ref<string>('')
const addCallLink = ref<boolean>(true)
const customParameters = ref<ParameterOption[]>([])

// AI Templates state
const aiTemplates = ref<AiTemplate[]>([])
const selectedTemplateId = ref<number | null>(null)
const isLoadingTemplates = ref(false)

// Fetch AI templates
async function fetchAiTemplates () {
    isLoadingTemplates.value = true
    try {
        aiTemplates.value = await widgetParameterApi.getAiTemplateList()
    } finally {
        isLoadingTemplates.value = false
    }
}

// Handle template selection from toolbar dropdown
function onTemplateSelected (template: AiTemplate) {
    selectedTemplateId.value = template.TemplateID
    // Replace escaped newlines with actual newlines for proper markdown rendering
    const content = template.Template.replace(/\\n/g, '\n')
    templateValue.value = content
    markdownEditorRef.value?.setMarkdown(content)
}

// Initialize state from config
function initializeFromConfig () {
    const valueJson = props.config?.WidgetParameterValueJson as AiParameterValueJson | undefined

    // Reset to defaults first
    selectedMode.value = 'single'
    singleValue.value = ''
    multipleValue.value = []
    templateValue.value = ''
    addCallLink.value = true
    customParameters.value = []
    selectedTemplateId.value = null

    if (valueJson) {
        const mode = valueJson.type || 'single'
        selectedMode.value = mode

        // Load data based on type
        if (mode === 'single') {
            singleValue.value = typeof valueJson.data === 'string' ? valueJson.data : ''
        } else if (mode === 'multiple') {
            multipleValue.value = Array.isArray(valueJson.data) ? valueJson.data : []
        } else if (mode === 'template') {
            templateValue.value = typeof valueJson.data === 'string' ? valueJson.data : ''
        }

        addCallLink.value = valueJson.addCallLink !== false

        // Load custom parameters if present
        customParameters.value = cloneDeep(valueJson.customParameters || [])
    }

    fetchAiTemplates()
}

function onTemplateChange (val: string) {
    templateValue.value = val
}

function onInsertParameter (text: string) {
    markdownEditorRef.value?.insertText(text)
}

function addCustomParameter (param: ParameterOption) {
    customParameters.value.push(param)
}

function removeCustomParameter (param: ParameterOption) {
    customParameters.value = customParameters.value.filter(p => p.value !== param.value)
}

function getFirstAvailableOption (): string | null {
    const firstGroup = parameterGroups.value.find(group => group.options.length > 0)
    return firstGroup?.options[0]?.value || null
}

function save () {
    // Build simplified data structure based on selected mode
    let data: string | string[]

    switch (selectedMode.value) {
        case 'single':
            // If no value is selected, auto-select the first available option
            if (!singleValue.value) {
                const firstOption = getFirstAvailableOption()
                if (firstOption) {
                    singleValue.value = firstOption
                }
            }
            data = singleValue.value
            break
        case 'multiple':
            // If no values are selected, auto-select the first available option
            if (multipleValue.value.length === 0) {
                const firstOption = getFirstAvailableOption()
                if (firstOption) {
                    multipleValue.value = [firstOption]
                }
            }
            // multipleValue preserves the order from drag reorder
            data = [ ...multipleValue.value ]
            break
        case 'template':
            data = templateValue.value
            break
        default:
            data = ''
    }

    const valueJson: AiParameterValueJson = {
        type: selectedMode.value,
        data
    }

    // Only include customParameters if there are any
    if (customParameters.value.length > 0) {
        valueJson.customParameters = cloneDeep(customParameters.value)
    }

    valueJson.addCallLink = addCallLink.value

    const newConfig: WidgetConfig = {
        ...props.config,
        WidgetParameterValueJson: valueJson
    }

    emit('save', newConfig)
    emit('update:visible', false)
}

function cancel () {
    closeWithConfirm(() => emit('update:visible', false))
}

function onClose (val: boolean) {
    if (!val) cancel()
}

// Initialize when modal opens
watch(
    () => props.visible,
    (open) => {
        if (open) {
            initializeFromConfig()
        }
    },
    { immediate: true }
)

</script>

<style lang="scss" scoped>
.ai-parameter-modal {
    .mode-selector {
        :deep(.base-radio-group) {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
        }
    }
}
</style>
